<!-- fork overlay: chatwoot-br 04b992483 (blob:0cd7c76d081adf8e) | OSS baseline: v4.13.0 (blob:58a16c8326110d3a) | Ported: 2026-06-10 -->
<script setup>
// fork overlay: wholesale replacement of OSS v4.13.0 WhatsAppCampaignForm
// (FEAT-002 + EXT-001, issue #18). Adds plain-text one-off support for
// whatsapp_web inboxes — gowa has no Meta templates, so when the selected
// inbox's provider is `whatsapp_web` the template selector is replaced by a
// free-text message + the EXT-001 send-delay block, and `template_params` is
// omitted. whatsapp_cloud keeps the upstream template flow unchanged.
//
// D-07: alias-form imports so the fork-overlay plugin resolves OSS siblings.
// i18n: reuses the already-translated CAMPAIGN.API.CREATE.FORM.{MESSAGE,DELAY}
// keys (generic strings) for the plain-text branch to avoid duplicating keys.
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  requiredIf,
  minLength,
  maxLength,
  numeric,
  minValue,
  maxValue,
} from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const MESSAGE_MAX_LENGTH = 150000;
const DELAY_MIN = '0';
const DELAY_MAX = '300';

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  message: '',
  scheduledAt: null,
  selectedAudience: [],
  delayType: 'none',
  delaySeconds: 5,
  delayMin: 3,
  delayMax: 10,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const selectedInbox = computed(
  () =>
    formState.inboxes.value?.find(inbox => inbox.id === state.inboxId) ?? null
);

// gowa (whatsapp_web) has no Meta templates → plain-text one-off path.
const isWhatsAppWeb = computed(
  () => selectedInbox.value?.provider === 'whatsapp_web'
);

const isFixedDelay = computed(() => state.delayType === 'fixed');
const isRandomDelay = computed(() => state.delayType === 'random');

// EXT-001: the delay range validators must only apply on the active delay path
// (whatsapp_web + the chosen delay type). requiredIf already gates the
// "required" rule, but numeric/minValue/maxValue were ungated and gteMin was
// gated only on delayType — so a stale out-of-range delay left over from a
// whatsapp_web selection kept v$.$invalid true after switching to a cloud
// inbox, where the delay block and its error message unmount, silently
// dead-disabling the submit button. Gate every range rule on isWhatsAppWeb +
// delayType so inactive delay fields never block submit.
const onlyWhen = (active, rule) => value => !active() || rule(value);
const whenFixed = () => isWhatsAppWeb.value && state.delayType === 'fixed';
const whenRandom = () => isWhatsAppWeb.value && state.delayType === 'random';

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  // Template required only once a non-web (cloud) inbox is selected.
  templateId: {
    requiredForCloud: requiredIf(() => !!state.inboxId && !isWhatsAppWeb.value),
  },
  // Free-text message required only for the whatsapp_web path.
  message: {
    requiredIfWeb: requiredIf(() => isWhatsAppWeb.value),
    maxLength: maxLength(MESSAGE_MAX_LENGTH),
  },
  scheduledAt: { required },
  selectedAudience: { required },
  delaySeconds: {
    requiredIfFixed: requiredIf(whenFixed),
    numeric: onlyWhen(whenFixed, numeric),
    minValue: onlyWhen(whenFixed, minValue(0)),
    maxValue: onlyWhen(whenFixed, maxValue(300)),
  },
  delayMin: {
    requiredIfRandom: requiredIf(whenRandom),
    numeric: onlyWhen(whenRandom, numeric),
    minValue: onlyWhen(whenRandom, minValue(0)),
    maxValue: onlyWhen(whenRandom, maxValue(300)),
  },
  delayMax: {
    requiredIfRandom: requiredIf(whenRandom),
    numeric: onlyWhen(whenRandom, numeric),
    minValue: onlyWhen(whenRandom, minValue(0)),
    maxValue: onlyWhen(whenRandom, maxValue(300)),
    gteMin: value => !whenRandom() || Number(value) >= Number(state.delayMin),
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const delayTypeOptions = computed(() => [
  { value: 'none', label: t('CAMPAIGN.API.CREATE.FORM.DELAY.TYPES.NONE') },
  { value: 'fixed', label: t('CAMPAIGN.API.CREATE.FORM.DELAY.TYPES.FIXED') },
  { value: 'random', label: t('CAMPAIGN.API.CREATE.FORM.DELAY.TYPES.RANDOM') },
]);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const messageError = computed(() =>
  v$.value.message.$error ? t('CAMPAIGN.API.CREATE.FORM.MESSAGE.ERROR') : ''
);

const delayError = computed(() => {
  const invalid =
    v$.value.delaySeconds.$error ||
    v$.value.delayMin.$error ||
    v$.value.delayMax.$error;
  return invalid ? t('CAMPAIGN.API.CREATE.FORM.DELAY.ERROR') : '';
});

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const buildDelayConfig = () => {
  if (state.delayType === 'fixed') {
    return { type: 'fixed', seconds: Number(state.delaySeconds) };
  }
  if (state.delayType === 'random') {
    return {
      type: 'random',
      min: Number(state.delayMin),
      max: Number(state.delayMax),
    };
  }
  return { type: 'none' };
};

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const base = {
    title: state.title,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };

  // whatsapp_web: plain text + EXT-001 delay, no template_params.
  if (isWhatsAppWeb.value) {
    return {
      ...base,
      message: state.message,
      trigger_rules: { delay: buildDelayConfig() },
    };
  }

  // whatsapp_cloud: unchanged upstream template payload.
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;
  const templateContent = parserData?.renderedTemplate || '';
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    ...base,
    message: templateContent,
    template_params: templateParams,
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset the template/message selection when the inbox (and thus provider) changes.
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);

// Exposed for unit tests — script-setup internals are otherwise private.
defineExpose({
  state,
  isWhatsAppWeb,
  inboxOptions,
  audienceList,
  buildDelayConfig,
  prepareCampaignDetails,
  isSubmitDisabled,
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- whatsapp_cloud: template selector + parser (only after a cloud inbox is picked) -->
    <template v-if="state.inboxId && !isWhatsAppWeb">
      <div class="flex flex-col gap-1">
        <label
          for="template"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
        </label>
        <ComboBox
          id="template"
          v-model="state.templateId"
          :options="templateOptions"
          :has-error="!!formErrors.template"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
          :message="formErrors.template"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
        </p>
      </div>

      <WhatsAppTemplateParser
        v-if="selectedTemplate"
        ref="templateParserRef"
        :template="selectedTemplate"
      />
    </template>

    <!-- whatsapp_web (gowa): plain-text message + EXT-001 send delay -->
    <template v-else-if="isWhatsAppWeb">
      <TextArea
        v-model="state.message"
        :label="t('CAMPAIGN.API.CREATE.FORM.MESSAGE.LABEL')"
        :placeholder="t('CAMPAIGN.API.CREATE.FORM.MESSAGE.PLACEHOLDER')"
        :max-length="MESSAGE_MAX_LENGTH"
        show-character-count
        :message="messageError"
        :message-type="messageError ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-1">
        <label
          for="delayType"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.API.CREATE.FORM.DELAY.LABEL') }}
        </label>
        <ComboBox
          id="delayType"
          v-model="state.delayType"
          :options="delayTypeOptions"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.API.CREATE.FORM.DELAY.INFO') }}
        </p>

        <Input
          v-if="isFixedDelay"
          v-model="state.delaySeconds"
          type="number"
          :min="DELAY_MIN"
          :max="DELAY_MAX"
          :label="t('CAMPAIGN.API.CREATE.FORM.DELAY.SECONDS.LABEL')"
          :placeholder="t('CAMPAIGN.API.CREATE.FORM.DELAY.SECONDS.PLACEHOLDER')"
          class="mt-2"
        />

        <div v-if="isRandomDelay" class="flex gap-3 mt-2">
          <Input
            v-model="state.delayMin"
            type="number"
            :min="DELAY_MIN"
            :max="DELAY_MAX"
            :label="t('CAMPAIGN.API.CREATE.FORM.DELAY.MIN.LABEL')"
            :placeholder="t('CAMPAIGN.API.CREATE.FORM.DELAY.MIN.PLACEHOLDER')"
            class="w-full"
          />
          <Input
            v-model="state.delayMax"
            type="number"
            :min="DELAY_MIN"
            :max="DELAY_MAX"
            :label="t('CAMPAIGN.API.CREATE.FORM.DELAY.MAX.LABEL')"
            :placeholder="t('CAMPAIGN.API.CREATE.FORM.DELAY.MAX.PLACEHOLDER')"
            class="w-full"
          />
        </div>
        <p v-if="delayError" class="mt-1 text-xs text-n-ruby-9">
          {{ delayError }}
        </p>
      </div>
    </template>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
